import React, { useContext, useEffect } from "react";
import {
  Loader,
  TextButton,
  ModalContext,
  ModalButtonsContainer,
  useIcon,
  showErrorDialog,
} from "@SeemplicityDev/shared";
import DependenciesModal from "../../../../AppView/Dependencies/DependenciesModal";
import useLogger from "../../../../Logger/useLogger";
import { useScopeDependencies } from "./useScopeDependencies";
import { useDeleteScope } from "./useScopeGuardrailMutations";

export interface DeleteScopeModalProps {
  scopeId: string;
  scopeName?: string;
  onDeleted: () => void;
}

const DeleteScopeModal: React.FC<DeleteScopeModalProps> = ({
  scopeId,
  scopeName,
  onDeleted,
}) => {
  const { closeModal } = useContext(ModalContext);
  const { getSvg } = useIcon();
  const { logWarning } = useLogger();
  const { check, loading, called, dependencies, error } =
    useScopeDependencies();
  const { deleteScope, loading: deleting } = useDeleteScope();

  useEffect(() => {
    check({ id: scopeId, nodeType: "scope" });
  }, [scopeId, check]);

  const recheck = () => check({ id: scopeId, nodeType: "scope" });

  const confirmDelete = () => {
    deleteScope(scopeId)
      .then((ok) => {
        if (!ok) {
          recheck();
          return;
        }
        onDeleted();
        closeModal();
      })
      .catch(async (err: unknown) => {
        // Warning, not a Sentry error: the BE logs the refusal; this is the FE trace in DD.
        logWarning(err, "Scope guardrails: delete_finding_scope_v3 failed");
        // A dependency block arrives as a rejection, so re-check before
        // reporting: if the scope is now in use the blocker explains itself.
        // Only an unexplained failure gets the generic dialog; a failed
        // re-check shows the modal's own retry state.
        const result = await recheck();
        if (
          result.status === "ok" &&
          (result.dependencies?.direct?.total_count ?? 0) === 0
        ) {
          showErrorDialog({
            message: "Couldn't delete this scope. Please try again.",
          });
        }
      });
  };

  if (!called || loading) {
    return (
      <div className={"modal-text delete-filter-modal is-centered"}>
        <Loader
          center
          img={getSvg("icon-spinner")}
          fullHeight
          iconSize={"xxxxl"}
          title={"Checking connections..."}
          subtitle={
            "Please wait while we verify if this scope is linked to any items."
          }
        />
      </div>
    );
  }

  if (error || !dependencies) {
    return (
      <div className={"modal-text delete-filter-modal is-centered"}>
        <Loader
          center
          title={"Couldn't check this scope's connections."}
          subtitle={"Please try again."}
        />
        <ModalButtonsContainer centerButtons>
          <TextButton
            buttonStyle={"secondary"}
            label={"Close"}
            onClick={closeModal}
            data-testid={"close-delete-scope"}
          />
          <TextButton
            buttonStyle={"primary"}
            label={"Retry"}
            onClick={recheck}
            data-testid={"retry-scope-dependencies"}
          />
        </ModalButtonsContainer>
      </div>
    );
  }

  if ((dependencies.direct?.total_count ?? 0) > 0) {
    return (
      <div className={"delete-filter-modal"}>
        <DependenciesModal
          entityName={scopeName}
          dependencies={dependencies.direct}
        />
        <ModalButtonsContainer spaceBetween>
          <TextButton
            buttonStyle={"textLink"}
            leftIcon={"seem-icon-refresh"}
            label={"Refresh the Data"}
            onClick={recheck}
            data-testid={"refresh-scope-dependencies"}
          />
          <TextButton
            buttonStyle={"secondary"}
            label={"Close"}
            onClick={closeModal}
            data-testid={"close-delete-scope"}
          />
        </ModalButtonsContainer>
      </div>
    );
  }

  return (
    <div className={"modal-text delete-filter-modal is-centered is-confirm"}>
      <div className={"margin-bottom-20 delete-filter-confirm"}>
        {"Are you sure you want to delete "}
        <span className={"modal-live-text"}>{scopeName}</span>
        {" scope?"}
      </div>
      <ModalButtonsContainer centerButtons>
        <TextButton
          buttonStyle={"secondary"}
          label={"Cancel"}
          onClick={closeModal}
          disabled={deleting}
          data-testid={"cancel-delete"}
        />
        <TextButton
          buttonStyle={"danger"}
          label={"Delete"}
          onClick={confirmDelete}
          disabled={deleting}
          data-testid={"confirm-delete"}
        />
      </ModalButtonsContainer>
    </div>
  );
};

export default DeleteScopeModal;
